import {clsx} from 'clsx'
import {memo, use} from 'react'
import type {AnchorHTMLAttributes, ReactNode, Ref} from 'react'
import {Link} from '@github-ui/react-router'
import {useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {SidebarTooltip} from '../SidebarTooltip/SidebarTooltip'
import {GroupedContext} from './sidebar-context'
import {getCurrentState, sidebarChildKind} from './contains-current'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import styles from './Item.module.css'
import rowStyles from './Row.module.css'

type SidebarItemLinkProps =
  | {href: string; to?: never; forceHardNavigation?: never}
  | {to: string; href?: never; forceHardNavigation?: boolean}

export type ItemProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> &
  SidebarItemLinkProps & {
    children: ReactNode
    leadingVisual?: ReactNode
    trailingVisual?: ReactNode
    trailingAction?: ReactNode
    actionVisibility?: 'always' | 'hover'
    tooltipText?: string
    current?: boolean
    ref?: Ref<HTMLAnchorElement>
  }

export const Item = Object.assign(
  memo(function SidebarItem({
    children,
    href,
    to,
    forceHardNavigation,
    leadingVisual,
    trailingVisual,
    trailingAction,
    actionVisibility = 'always',
    tooltipText,
    current,
    'aria-current': ariaCurrent,
    ref,
    className,
    ...linkProps
  }: ItemProps) {
    const {visualIsExpanded} = useCollapsibleSidebarContext()
    const grouped = use(GroupedContext)
    const currentState = getCurrentState({current, 'aria-current': ariaCurrent})
    if (!grouped && !leadingVisual) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Sidebar.Item] Items outside a SectionGroup or Accordion need leadingVisual to appear in the rail. This item is only shown when the sidebar is expanded.',
        )
      }
      if (!visualIsExpanded) return null
    }
    const content = (
      <>
        {leadingVisual ? (
          <span className={clsx(styles.LeadingVisual, !grouped && rowStyles.Icon)}>{leadingVisual}</span>
        ) : null}
        <span
          data-sidebar-collapse-content
          className={clsx(rowStyles.Label, !visualIsExpanded && styles.visuallyHidden)}
        >
          {children}
        </span>
        {visualIsExpanded && trailingVisual ? (
          <span data-sidebar-collapse-content className={styles.TrailingVisual}>
            {trailingVisual}
          </span>
        ) : null}
      </>
    )
    const anchorProps = {
      ...linkProps,
      ref,
      'aria-current': currentState.ariaCurrent,
      className: clsx(
        rowStyles.Row,
        styles.Link,
        currentState.current && rowStyles.current,
        grouped && !leadingVisual && rowStyles.textLed,
        className,
      ),
    }
    const link =
      to && !forceHardNavigation ? (
        <Link {...anchorProps} to={to}>
          {content}
        </Link>
      ) : (
        <a {...anchorProps} href={href ?? to}>
          {content}
        </a>
      )
    return (
      <div
        className={clsx(styles.Item, actionStyles.actionRow, currentState.current && styles.current)}
        data-component="Sidebar.Item"
      >
        {tooltipText || typeof children === 'string' ? (
          <SidebarTooltip text={tooltipText ?? (children as string)} disabled={visualIsExpanded}>
            {link}
          </SidebarTooltip>
        ) : (
          link
        )}
        {visualIsExpanded && trailingAction ? (
          <SidebarAction hover={actionVisibility === 'hover'}>{trailingAction}</SidebarAction>
        ) : null}
      </div>
    )
  }),
  {[sidebarChildKind]: 'item' as const},
)
